import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { CreateBookingInput, UpdateBookingInput } from '@/lib/marina/validators'

export type BookingStatus =
  | 'PENDING'
  | 'WAITLISTED'
  | 'CONFIRMED'
  | 'DECLINED'
  | 'RESERVED'
  | 'CHECKED_IN'
  | 'CHECKED_OUT'
  | 'CANCELLED'
  | 'NO_SHOW'

export type BookingChannel = 'WALK_IN' | 'RADIO' | 'EMAIL' | 'PHONE' | 'TEXT_MESSAGE' | 'AGENT' | 'OTHER'

export interface Booking {
  id: string
  code: string
  customerId: string
  customer?: {
    id: string
    firstName: string
    lastName: string
    category: string
    email?: string | null
  }
  boatId: string
  boat?: { id: string; name: string; type?: string; loaMeters?: string }
  berthId: string | null
  berth?: { id: string; code: string; pontoon?: { code: string; name: string | null } } | null
  checkInAt: string
  checkOutAt: string
  actualCheckIn: string | null
  actualCheckOut: string | null
  channel: BookingChannel
  channelLocation: string | null
  status: BookingStatus
  notes: string | null
  totalAmountIdr: string | null
  createdAt: string
  updatedAt: string
  absences?: {
    id: string
    departure: string
    returnDate: string
    reason: string | null
    status: 'AVAILABLE' | 'BLOCKED'
  }[]
  quotes?: { id: string; code: string; status: string; totalIdr: string }[]
  invoices?: { id: string; code: string; status: string; totalIdr: string }[]
}

export interface BookingsListResponse {
  items: Booking[]
  total: number
  page: number
  pageSize: number
}

export interface BookingsListParams {
  q?: string
  status?: BookingStatus
  view?: 'requests' | 'bookings'
  customerId?: string
  boatId?: string
  berthId?: string
  channel?: BookingChannel
  from?: string
  to?: string
  page?: number
  pageSize?: number
  sort?: string
  order?: 'asc' | 'desc'
}

async function apiFetch<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await fetch(path, { credentials: 'include', ...init })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

async function jsonFetch<T>(path: string, init: RequestInit): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  })
  if (!res.ok) {
    const err = await res.json().catch(() => ({ error: 'Request failed' }))
    throw new Error(err.error || `HTTP ${res.status}`)
  }
  return res.json()
}

function toQuery(params: Record<string, unknown>): string {
  const qs = new URLSearchParams()
  for (const [k, v] of Object.entries(params)) {
    if (v !== undefined && v !== null && v !== '') qs.set(k, String(v))
  }
  const s = qs.toString()
  return s ? `?${s}` : ''
}

export function useBookings(params: BookingsListParams = {}) {
  return useQuery({
    queryKey: ['bookings', params],
    queryFn: () => apiFetch<BookingsListResponse>(`/api/bookings${toQuery(params as Record<string, unknown>)}`),
    staleTime: 15_000,
  })
}

export function useBookingStatusCounts() {
  return useQuery({
    queryKey: ['bookings', 'status-counts'],
    queryFn: () => apiFetch<{ counts: Record<string, number> }>('/api/bookings/status-counts'),
    staleTime: 15_000,
  })
}

export function useBooking(id: string | null | undefined) {
  return useQuery({
    queryKey: ['booking', id],
    queryFn: () => apiFetch<{ booking: Booking }>(`/api/bookings/${id}`),
    enabled: !!id,
  })
}

export function useCreateBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: CreateBookingInput) =>
      jsonFetch<{ booking: Booking }>('/api/bookings', { method: 'POST', body: JSON.stringify(data) }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}

export function useUpdateBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateBookingInput }) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}`, { method: 'PATCH', body: JSON.stringify(data) }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: ['bookings'] })
      qc.invalidateQueries({ queryKey: ['booking', vars.id] })
    },
  })
}

export function useDeleteBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => jsonFetch<{ ok: boolean }>(`/api/bookings/${id}`, { method: 'DELETE' }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
  })
}

function invalidateBooking(qc: ReturnType<typeof useQueryClient>, id: string) {
  qc.invalidateQueries({ queryKey: ['bookings'] })
  qc.invalidateQueries({ queryKey: ['booking', id] })
}

export function useApproveBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/approve`, { method: 'POST', body: '{}' }),
    onSuccess: (_, id) => invalidateBooking(qc, id),
  })
}
export function useWaitlistBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/waitlist`, { method: 'POST', body: '{}' }),
    onSuccess: (_, id) => invalidateBooking(qc, id),
  })
}
export function useDeclineBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/decline`, {
        method: 'POST',
        body: JSON.stringify({ reason }),
      }),
    onSuccess: (_, vars) => invalidateBooking(qc, vars.id),
  })
}
export function useCancelBooking() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/cancel`, { method: 'POST', body: '{}' }),
    onSuccess: (_, id) => invalidateBooking(qc, id),
  })
}
export function useAssignBerth() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, berthId }: { id: string; berthId: string }) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/assign-berth`, {
        method: 'POST',
        body: JSON.stringify({ berthId }),
      }),
    onSuccess: (_, vars) => invalidateBooking(qc, vars.id),
  })
}
export function useCheckIn() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, at, berthId, notes }: { id: string; at?: string; berthId?: string; notes?: string }) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/check-in`, {
        method: 'POST',
        body: JSON.stringify({ at, berthId, notes }),
      }),
    onSuccess: (_, vars) => invalidateBooking(qc, vars.id),
  })
}
export function useCheckOut() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, at, notes }: { id: string; at?: string; notes?: string }) =>
      jsonFetch<{ booking: Booking }>(`/api/bookings/${id}/check-out`, {
        method: 'POST',
        body: JSON.stringify({ at, notes }),
      }),
    onSuccess: (_, vars) => invalidateBooking(qc, vars.id),
  })
}
